import type { Mutacion } from '@barbones/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CloudOff, RefreshCw, TriangleAlert } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { usuarioActualId } from '../cliente';
import { colaWeb } from './cola';

interface EstadoSincronizacion {
  enLinea: boolean;
  pendientes: number;
  conflictos: Mutacion[];
  sincronizar: () => Promise<void>;
  descartar: (id: string) => Promise<void>;
}

const Contexto = createContext<EstadoSincronizacion | null>(null);

/** Envía la cola al volver la red, cada 20 s y al volver a la pestaña; expone el estado al resto de la app. */
export function SincronizacionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [enLinea, setEnLinea] = useState(() => navigator.onLine);
  const [pendientes, setPendientes] = useState(0);
  const [conflictos, setConflictos] = useState<Mutacion[]>([]);

  const sincronizar = useCallback(async () => {
    const antes = (await colaWeb.pendientes()).length;
    if (antes > 0 && navigator.onLine) await colaWeb.procesar();
    const despues = (await colaWeb.pendientes()).length;
    setPendientes(despues);
    setConflictos((await colaWeb.conflictos()).filter((m) => m.propietario === usuarioActualId()));
    if (despues < antes) void qc.invalidateQueries();
  }, [qc]);

  const descartar = useCallback(async (id: string) => {
    await colaWeb.descartarConflicto(id);
    setConflictos((await colaWeb.conflictos()).filter((m) => m.propietario === usuarioActualId()));
  }, []);

  useEffect(() => {
    const cambio = () => {
      setEnLinea(navigator.onLine);
      if (navigator.onLine) void sincronizar();
    };
    const visible = () => document.visibilityState === 'visible' && void sincronizar();
    window.addEventListener('online', cambio);
    window.addEventListener('offline', cambio);
    document.addEventListener('visibilitychange', visible);
    const intervalo = setInterval(() => void sincronizar(), 20_000);
    const inicial = setTimeout(() => void sincronizar(), 0);
    return () => {
      window.removeEventListener('online', cambio);
      window.removeEventListener('offline', cambio);
      document.removeEventListener('visibilitychange', visible);
      clearInterval(intervalo);
      clearTimeout(inicial);
    };
  }, [sincronizar]);

  const valor = useMemo(() => ({ enLinea, pendientes, conflictos, sincronizar, descartar }), [enLinea, pendientes, conflictos, sincronizar, descartar]);
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSincronizacion(): EstadoSincronizacion {
  const ctx = useContext(Contexto);
  if (!ctx) throw new Error('useSincronizacion debe usarse dentro de <SincronizacionProvider>');
  return ctx;
}

/** Franja superior: sin conexión, cambios por enviar o rechazados por el servidor. */
export function BannerConexion() {
  const { enLinea, pendientes, conflictos, sincronizar } = useSincronizacion();
  if (enLinea && pendientes === 0 && conflictos.length === 0) return null;

  if (conflictos.length > 0) {
    return (
      <div role="status" className="flex items-center justify-center gap-2 bg-destructive-soft px-4 py-1.5 text-[13px] text-destructive">
        <TriangleAlert className="size-4" aria-hidden />
        {conflictos.length} cambio(s) hechos sin conexión no se pudieron aplicar.
        <Link to="/sincronizacion" className="font-medium underline underline-offset-2">
          Revisar
        </Link>
      </div>
    );
  }
  return (
    <div role="status" className="flex items-center justify-center gap-2 bg-warning-soft px-4 py-1.5 text-[13px] text-warning">
      <CloudOff className="size-4" aria-hidden />
      {enLinea ? `Enviando ${pendientes} cambio(s) guardados sin conexión…` : 'Sin conexión. Puedes seguir con agenda y clientes; caja y cobros esperan a la red.'}
      {pendientes > 0 && !enLinea && <span className="font-medium">· {pendientes} por enviar</span>}
      {enLinea && (
        <button type="button" className="inline-flex cursor-pointer items-center gap-1 font-medium underline underline-offset-2" onClick={() => void sincronizar()}>
          <RefreshCw className="size-3.5" aria-hidden /> Reintentar
        </button>
      )}
    </div>
  );
}
